// What the browser and its extensions may do to a text field: offer saved
// logins, fill a person's details, correct spelling, capitalise.
//
// Two rules, one module:
//
//  1. A password manager comes only where a field SAYS it holds a credential
//     or a person's details — an `autocomplete` token such as 'username',
//     'current-password' or 'email' (the sign-in and password pages, the
//     profile), or 'on' (a card element whose author allowed autofill).
//     Every other field is opted out in every vendor's dialect.
//     `installFieldGuard` enforces it for the whole page, so a field nobody
//     thought about — a group's name on the Sharing tab — never draws
//     1Password's "Sign in" menu.
//
//  2. A field whose text is a query or an identifier — a search box, a
//     filter, KQL, an email address, a key, a code — gets no autocorrect,
//     autocapitalise or spellcheck. Autocorrect rewrites a half-typed search
//     term when focus leaves the box, and the match the person was about to
//     click disappears. Plain inputs spread `NO_TEXT_ASSIST`; the search box
//     inside every Select, AutoComplete and TreeSelect is reached by the guard
//     (antd passes no props through to it). Prose — a name, a note, a
//     description — keeps them.
//
// docs/platform-standards.md § Text fields states both for authors.

// Vendor opt-out attributes. `autocomplete='off'` is the only STANDARD lever
// and the browsers themselves honor it, but a password manager is an extension
// running its own field heuristics over the DOM — it never sees the standard
// attribute as the last word. Each vendor ships its own marker instead, and
// every one of them tests for the attribute's PRESENCE, so the values here are
// cosmetic. Unknown to a given extension, these are inert data-* attributes.
//
// Sourced from each vendor's own developer guidance; if one of them renames
// its marker, the field degrades to plain `autocomplete='off'` rather than
// breaking. Add new vendors here and every field picks them up.
export const AUTOFILL_OPT_OUT_ATTRS = {
	'data-1p-ignore': 'true',           // 1Password
	'data-lpignore': 'true',            // LastPass
	'data-bwignore': 'true',            // Bitwarden
	'data-protonpass-ignore': 'true',   // Proton Pass
	'data-form-type': 'other',          // Dashlane
};

// Spread onto an input whose text is a query or an identifier (rule 2).
// `autoCorrect` is WebKit's own attribute; Chrome's autocorrect follows
// `spellCheck`, so it takes both to stop it everywhere.
export const NO_TEXT_ASSIST = {
	autoComplete: 'off',
	autoCorrect: 'off',
	autoCapitalize: 'none',
	spellCheck: false,
};

// Input types that hold no typed text, so nothing here applies to them.
const NOT_TEXT = new Set([
	'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

const isTextField = el => {
	const tag = el?.tagName;
	if (tag === 'TEXTAREA') return true;
	if (tag !== 'INPUT') return false;
	return !NOT_TEXT.has((el.getAttribute('type') || 'text').toLowerCase());
};

// A token other than 'off' is the field asking for help, on purpose.
const asksForAutofill = el => {
	const token = (el.getAttribute('autocomplete') || '').trim().toLowerCase();
	return token !== '' && token !== 'off';
};

// The search box of a Select / AutoComplete / TreeSelect, or a native search.
const isLookup = el => el.getAttribute('role') === 'combobox'
	|| (el.getAttribute('type') || '').toLowerCase() === 'search';

// Markers this module added (never ones React rendered), so they can be taken
// back if the field later asks for autofill.
const stamped = new WeakSet();

const setIfAbsent = (el, name, value) => {
	if (!el.hasAttribute(name)) el.setAttribute(name, value);
};

// Bring one field in line with both rules. Idempotent, and it only ever adds
// what the field left unsaid: an attribute React rendered is never overwritten.
export function guardField ( el ) {
	if (!isTextField(el)) return;

	if (asksForAutofill(el)) {
		if (stamped.has(el)) {
			Object.keys(AUTOFILL_OPT_OUT_ATTRS).forEach(name => el.removeAttribute(name));
			stamped.delete(el);
		}
	} else {
		setIfAbsent(el, 'autocomplete', 'off');
		if (!el.hasAttribute('data-1p-ignore')) stamped.add(el);
		Object.entries(AUTOFILL_OPT_OUT_ATTRS).forEach(([name, value]) => setIfAbsent(el, name, value));
	}

	if (isLookup(el)) {
		setIfAbsent(el, 'autocorrect', 'off');
		setIfAbsent(el, 'autocapitalize', 'none');
		setIfAbsent(el, 'spellcheck', 'false');
	}
}

const guardTree = node => {
	if (node?.nodeType !== 1) return;
	guardField(node);
	node.querySelectorAll?.('input, textarea').forEach(guardField);
};

// Watch the document from here on: every field as it is added, again if its
// `autocomplete` or `role` changes, and once more as it takes focus. The focus
// pass is the backstop for a field focused inside the same commit that adds
// it (`autoFocus`), before the observer's callback runs; it listens on the
// window in the capture phase, so it runs before an extension's own listener
// on the document or the field. Returns a function that stops it.
export function installFieldGuard ( doc = document ) {
	const view = doc.defaultView;
	if (!view?.MutationObserver) return () => {};

	guardTree(doc.body);

	const observer = new view.MutationObserver(records => {
		records.forEach(record => {
			if (record.type === 'attributes') guardField(record.target);
			else record.addedNodes.forEach(guardTree);
		});
	});
	observer.observe(doc.documentElement, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['autocomplete', 'role', 'type'],
	});

	const onFocus = event => guardField(event.target);
	view.addEventListener('focusin', onFocus, true);

	return () => {
		observer.disconnect();
		view.removeEventListener('focusin', onFocus, true);
	};
}
